import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, Package, ArrowUpRight, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageShell } from '@/components/brand/PageShell';
import { PageHeader } from '@/components/brand/PageHeader';
import { LoadingState, EmptyState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypeMeta = {
  Baby:      { label: 'Baby registry',     accent: 'bg-clay-100 text-clay-700' },
  Wedding:   { label: 'Wedding registry',  accent: 'bg-blush-100 text-clay-700' },
  Birthday:  { label: 'Birthday registry', accent: 'bg-cream-200 text-clay-700' },
  Custom:    { label: 'Custom registry',   accent: 'bg-sage-100 text-sage-700' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const RegistryCard = ({ registry, onView, onEdit, onDelete }) => {
  const meta = eventTypeMeta[registry.event_type] || eventTypeMeta.Custom;
  const itemCount = registry.items?.length || 0;
  const purchased = registry.items?.filter(
    (i) => i.status === 'purchased' || i.status === 'fully_funded'
  ).length || 0;
  const progress = itemCount > 0 ? Math.round((purchased / itemCount) * 100) : 0;

  return (
    <article
      className="group relative rounded-3xl bg-cream-50 border border-border/70 p-6 sm:p-7 shadow-soft-1 hover:shadow-soft-2 hover:border-clay-200 transition-all duration-300"
      data-testid={`registry-card-${registry.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className={`inline-block text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full ${meta.accent}`}>
          {meta.label}
        </span>
        <div className="flex items-start gap-2">
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ink-muted mb-1">Event</p>
            <p className="text-sm font-medium text-ink">{formatDate(registry.event_date)}</p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
            aria-label={`Delete ${registry.name || registry.event_type + ' registry'}`}
            title="Delete this registry"
            className="inline-flex items-center justify-center h-8 w-8 -mt-1 -mr-1 rounded-full text-ink-muted/70 hover:text-clay-700 hover:bg-clay-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-clay-500/40 transition-colors"
            data-testid={`delete-registry-btn-${registry.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <h3 className="mt-5 font-display text-2xl text-ink leading-snug line-clamp-2">
        {registry.name || `${registry.event_type} basket`}
      </h3>

      <div className="mt-5 flex items-center gap-5 text-sm text-ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" />
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(registry.created_at)}
        </span>
      </div>

      {/* Progress */}
      {itemCount > 0 && (
        <div className="mt-5">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[12px] text-ink-muted">{purchased} of {itemCount} gifted</span>
            <span className="text-[12px] font-medium text-clay-700">{progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden">
            <div
              className="h-full bg-clay-500 rounded-full transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-6 pt-5 border-t border-border/70 flex items-center gap-2">
        <BrandButton
          variant="outline"
          size="sm"
          onClick={onView}
          className="flex-1"
          data-testid={`view-registry-btn-${registry.id}`}
        >
          <ArrowUpRight className="h-3.5 w-3.5" /> View
        </BrandButton>
        <BrandButton
          variant="primary"
          size="sm"
          onClick={onEdit}
          className="flex-1"
          data-testid={`edit-registry-btn-${registry.id}`}
        >
          <Pencil className="h-3.5 w-3.5" /> Manage
        </BrandButton>
      </div>
    </article>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [registries, setRegistries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick-delete state
  const [pendingDelete, setPendingDelete] = useState(null); // the registry awaiting confirmation
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    fetchRegistries();
  }, [user, authLoading]); // eslint-disable-line

  const fetchRegistries = async () => {
    try {
      const response = await axios.get(`${API}/registry/user/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistries(response.data || []);
    } catch (error) {
      toast.error('Could not load your registries');
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/registry/${pendingDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Optimistic local removal — feels instant
      setRegistries((prev) => prev.filter((r) => r.id !== pendingDelete.id));
      toast.success('Registry deleted');
      setPendingDelete(null);
    } catch {
      toast.error('Could not delete the registry');
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <PageShell>
        <LoadingState label="Settling things in…" />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="container-page py-16 sm:py-24">
        <PageHeader
          eyebrow={user?.name ? `Hello, ${user.name.split(' ')[0]}` : 'Your registries'}
          title="Your baskets,"
          titleAccent="all in one place."
          lede="Each one quietly keeping track. Add to them, share them, watch the gifts come in."
          actions={
            <BrandButton
              size="lg"
              onClick={() => navigate('/registry/new')}
              data-testid="create-new-registry-btn"
            >
              <Plus className="h-4 w-4" />
              New registry
            </BrandButton>
          }
        />

        <div className="mt-14">
          {loading ? (
            <LoadingState label="Gathering your baskets…" compact />
          ) : registries.length === 0 ? (
            <EmptyState
              eyebrow="Nothing here yet"
              title="Your first basket is waiting."
              body="Begin a registry — name it, set a date, and add a few things you'd love. The rest unfolds quietly."
              action={
                <BrandButton
                  size="lg"
                  onClick={() => navigate('/registry/new')}
                  data-testid="empty-state-create-btn"
                >
                  Begin a registry
                </BrandButton>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {registries.map((registry) => (
                <RegistryCard
                  key={registry.id}
                  registry={registry}
                  onView={() => navigate(`/registry/${registry.id}`)}
                  onEdit={() => navigate(`/registry/${registry.id}/edit`)}
                  onDelete={() => setPendingDelete(registry)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick-delete confirmation */}
      <Dialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open && !deleting) setPendingDelete(null); }}
      >
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink">
              Delete {pendingDelete?.name || `${pendingDelete?.event_type || ''} basket`}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-ink-soft text-sm leading-relaxed">
            This is permanent — items, contributions, the share link, all of it.
            People who already have your link will see a "not found" page.
          </p>
          {(pendingDelete?.items?.length ?? 0) > 0 && (
            <div className="rounded-xl bg-clay-50 border border-clay-200 px-4 py-3 text-sm text-clay-700">
              You'll lose <span className="font-medium">{pendingDelete.items.length}</span>{' '}
              {pendingDelete.items.length === 1 ? 'item' : 'items'} in this basket.
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <BrandButton
              variant="outline"
              className="flex-1"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </BrandButton>
            <BrandButton
              onClick={executeDelete}
              disabled={deleting}
              loading={deleting}
              className="flex-1 !bg-clay-700 hover:!bg-clay-800"
              data-testid="confirm-delete-registry-btn"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </BrandButton>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default Dashboard;
